/*
 * <maceoo-cart-recommendations>: the "You May Also Like" rail in the cart drawer.
 *
 * Source: Shopify's /recommendations/products endpoint, rendered through sections/maceoo-cart-recommendations.liquid.
 * Complementary products (Search & Discovery) come first, then related products, for up to three of the newest cart
 * products (the "anchors" that sections/cart-drawer.liquid writes on the element). Products in the cart never show.
 *
 * How it stays smooth:
 * - Prestige rebuilds the drawer's whole DOM on every cart change. The rail (.mcr) is one live node kept by this
 *   module and moved into each rebuilt drawer from connectedCallback, which runs before the browser paints, so cards,
 *   decoded images and the scroll position carry over without a blank frame.
 * - While the shopper is looking at the rail it never reorders: a product that goes into the cart shows "Added", then
 *   folds away, and new candidates join at the end. The priority order is rebuilt only while the drawer is closed.
 * - Results are cached in sessionStorage for 15 minutes, prepared on idle after the page has loaded, and requested
 *   the moment an add-to-cart is clicked anywhere, so the rail is normally complete before the drawer opens. When it
 *   is not, a placeholder of the same size holds the space until every anchor has answered.
 */

const ROOT = window.Shopify?.routes?.root || "/";
const SECTION_ID = "maceoo-cart-recommendations";
const CACHE_TTL = 15 * 60 * 1000;
const RETRY_AFTER = 60 * 1000;
const ADDED_HOLD = 1100;
const PLACEHOLDER_TIMEOUT = 3000;
const PLACEHOLDER_COUNT = 3;
const WARM_IMAGES = 3;
const EASE = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";
const STORAGE_KEY = [
  "mcr:v1",
  window.Shopify?.country,
  window.Shopify?.currency?.active,
  window.Shopify?.locale,
].join(":");

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const inflight = new Map(); // "productId:intent" -> Promise<string[] | null>
const failedAt = new Map(); // anchor product id -> timestamp of the last failed request
const nodes = new Map(); // product id -> card element, reused so loaded images survive re-renders
const errorTimers = new WeakMap();
const watchedDrawers = new WeakSet();

const state = {
  host: null, // the <maceoo-cart-recommendations> currently in the drawer
  rail: null, // the live .mcr node moved from host to host
  shown: [], // product ids on the rail, in order
  seen: false, // the drawer is open and has painted, so the rail must not reorder
  added: null, // { id, timer }: product kept on the rail while it confirms "Added"
  addedIds: new Set(), // products added from the rail during this drawer session
  openCard: null, // card whose size sheet is open (touch)
  scrollLeft: 0,
  waitToken: null,
  idleQueued: false,
  cache: null,
};

/* Cache ------------------------------------------------------------------------------------------------------------ */

function cache() {
  if (!state.cache) {
    try {
      state.cache = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    } catch (error) {
      state.cache = null;
    }

    if (!state.cache?.recs || !state.cache?.cards) {
      state.cache = { recs: {}, cards: {} };
    }
  }

  return state.cache;
}

let persistTimer = 0;

function persist() {
  clearTimeout(persistTimer);

  persistTimer = setTimeout(() => {
    const { recs, cards } = cache();
    const now = Date.now();
    const referenced = new Set(state.shown);

    for (const [id, entry] of Object.entries(recs)) {
      if (now - entry.t > CACHE_TTL) {
        delete recs[id];
        continue;
      }

      entry.c.forEach((productId) => referenced.add(productId));
      entry.r.forEach((productId) => referenced.add(productId));
    }

    for (const id of Object.keys(cards)) {
      if (!referenced.has(id)) delete cards[id];
    }

    for (const id of nodes.keys()) {
      if (!cards[id] && !state.shown.includes(id)) nodes.delete(id);
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ recs, cards }));
    } catch (error) {
      // Storage full or blocked: the in-memory cache still serves this page.
    }
  }, 500);
}

/* Fetching --------------------------------------------------------------------------------------------------------- */

function fetchIntent(productId, intent, priority) {
  const key = `${productId}:${intent}`;

  if (!inflight.has(key)) {
    const url = `${ROOT}recommendations/products?product_id=${productId}&limit=10&intent=${intent}&section_id=${SECTION_ID}`;

    const request = fetch(url, { priority })
      .then((response) =>
        response.ok
          ? response.text()
          : Promise.reject(new Error(String(response.status))),
      )
      .then((html) => {
        const { cards } = cache();
        const doc = new DOMParser().parseFromString(html, "text/html");

        return Array.from(doc.querySelectorAll("[data-mcr-card]"), (card) => {
          const id = card.dataset.productId;
          cards[id] = card.outerHTML;

          // Fresh markup (price, sizes) replaces a parked card, never one the shopper is looking at.
          if (nodes.has(id) && !nodes.get(id).isConnected) nodes.delete(id);

          return id;
        });
      })
      .catch(() => null)
      .finally(() => inflight.delete(key));

    inflight.set(key, request);
  }

  return inflight.get(key);
}

function isFresh({ id, c }) {
  const entry = cache().recs[id];
  return Boolean(
    entry && Date.now() - entry.t < CACHE_TTL && entry.r && (!c || entry.c),
  );
}

function needsLoad(anchor) {
  return (
    !isFresh(anchor) &&
    Date.now() - (failedAt.get(anchor.id) || 0) > RETRY_AFTER
  );
}

/** Resolves to true when new recommendations were stored for the anchor. */
async function loadAnchor(anchor, priority = "low") {
  if (isFresh(anchor)) return false;

  const { recs } = cache();
  const previous = recs[anchor.id];
  const reusable =
    previous && Date.now() - previous.t < CACHE_TTL ? previous : {};

  const [complementary, related] = await Promise.all([
    reusable.c ||
      (anchor.c ? fetchIntent(anchor.id, "complementary", priority) : []),
    reusable.r || fetchIntent(anchor.id, "related", priority),
  ]);

  if (!complementary || !related) {
    failedAt.set(anchor.id, Date.now());
    return false;
  }

  recs[anchor.id] = {
    c: complementary,
    r: related,
    t: reusable.t || Date.now(),
  };
  persist();
  return true;
}

/** Start loading recommendations for a product the shopper is adding right now, from anywhere on the site. */
function warm(productId) {
  if (!/^\d+$/.test(productId || "")) return;

  const id = String(productId);

  loadAnchor({ id, c: true }, "high").then(() => {
    const entry = cache().recs[id];
    if (entry) preloadImages([...entry.c, ...entry.r].slice(0, WARM_IMAGES));
  });
}

/**
 * Start the first images while the cart is still updating. The cards are built now and parked; an <img> owned by
 * the page loads even before it is attached, and render() later attaches these same nodes.
 */
function preloadImages(ids) {
  ids.forEach((id) => {
    if (!cache().cards[id]) return;

    const node = cardNode(id);
    const image = node.querySelector(".mcr-card__image:not(.is-loaded)");
    if (node.isConnected || !image) return;

    image.fetchPriority = "low";
    image.loading = "eager";
    if (node.ownerDocument !== document) document.adoptNode(node);
  });
}

/* Choosing what to show -------------------------------------------------------------------------------------------- */

/** Complementary before related; within each, anchors take turns so every cart product is represented. */
function rankedCandidates(anchors, excluded) {
  const { recs, cards } = cache();
  const taken = new Set(excluded);
  const ranked = [];

  for (const kind of ["c", "r"]) {
    const lists = anchors.map(({ id }) => recs[id]?.[kind] || []);
    const longest = Math.max(0, ...lists.map((list) => list.length));

    for (let index = 0; index < longest; index++) {
      for (const list of lists) {
        const id = list[index];

        if (id && !taken.has(id) && cards[id]) {
          taken.add(id);
          ranked.push(id);
        }
      }
    }
  }

  return ranked;
}

function nextList(host) {
  const limit = host.limit;
  const excluded = new Set([...host.inCart, ...state.addedIds]);
  const ranked = rankedCandidates(host.anchors, excluded);

  if (!(state.seen && state.shown.length)) {
    return ranked.slice(0, limit);
  }

  // Visible: keep every card where it is, drop what went into the cart, append from the ranking.
  const held = state.added?.id;
  const { cards } = cache();
  const list = state.shown.filter(
    (id) => id === held || (!excluded.has(id) && (cards[id] || nodes.has(id))),
  );
  let count = list.filter((id) => id !== held).length;

  for (const id of ranked) {
    if (count >= limit) break;

    if (!list.includes(id)) {
      list.push(id);
      count++;
    }
  }

  return list;
}

/* Rendering -------------------------------------------------------------------------------------------------------- */

const track = () => state.rail.querySelector("[data-mcr-track]");
const hasPlaceholders = () =>
  Boolean(state.rail?.querySelector(".mcr-card--placeholder"));
const drawerOpen = () =>
  Boolean(state.host?.closest("cart-drawer")?.hasAttribute("open"));
const animated = () => state.seen && !reducedMotion.matches;

function cardNode(id) {
  let node = nodes.get(id);

  if (!node) {
    const template = document.createElement("template");
    template.innerHTML = cache().cards[id];
    node = template.content.firstElementChild;
    nodes.set(id, node);
  }

  if ("leaving" in node.dataset) {
    delete node.dataset.leaving;
    node.getAnimations().forEach((animation) => animation.cancel());
  }

  return node;
}

function resetCard(node) {
  node.classList.remove("is-open", "is-added", "is-busy", "has-error");
  node.querySelector("[data-mcr-open]")?.setAttribute("aria-expanded", "false");
}

function enter(node, index = 0) {
  node.animate(
    [
      { opacity: 0, transform: "translateX(0.75rem)" },
      { opacity: 1, transform: "none" },
    ],
    { duration: 400, delay: index * 60, easing: EASE, fill: "backwards" },
  );
}

function leave(node) {
  node.dataset.leaving = "";

  const gap = parseFloat(getComputedStyle(node.parentNode).columnGap) || 0;
  const width = node.getBoundingClientRect().width;
  const animation = node.animate(
    [
      { opacity: 1, flexBasis: `${width}px`, marginInlineEnd: "0px" },
      {
        opacity: 0,
        flexBasis: `${width}px`,
        marginInlineEnd: "0px",
        offset: 0.4,
      },
      { opacity: 0, flexBasis: "0px", marginInlineEnd: `${-gap}px` },
    ],
    { duration: 550, easing: EASE, fill: "forwards" },
  );

  animation.finished.then(
    () => {
      if (!("leaving" in node.dataset)) return; // brought back while it was leaving

      delete node.dataset.leaving;
      node.remove();
      animation.cancel();
      resetCard(node);
      updateNav();
    },
    () => {},
  );
}

function render(list) {
  const scroller = track();
  const wanted = new Set(list);
  const fromPlaceholder = hasPlaceholders();
  const wasEmpty = !state.shown.length;

  if (animated() && !fromPlaceholder && !wasEmpty) {
    for (const node of scroller.children) {
      if (!wanted.has(node.dataset.productId) && !("leaving" in node.dataset))
        leave(node);
    }

    list.forEach((id) => {
      const node = cardNode(id);

      if (node.parentNode !== scroller) {
        scroller.append(node);
        enter(node);
      }
    });
  } else {
    if (state.openCard && !wanted.has(state.openCard.dataset.productId))
      closeSheet();

    for (const node of scroller.children) {
      if (node.dataset.productId && !wanted.has(node.dataset.productId))
        resetCard(node);
    }

    const cards = list.map(cardNode);
    scroller.replaceChildren(...cards);

    if (animated() && fromPlaceholder) {
      // Same-size swap: the tinted boxes stay, text and images fade in on top of them.
      cards.forEach((card) => {
        card
          .querySelector(".product-card__info")
          ?.animate({ opacity: [0, 1] }, { duration: 300, easing: EASE });
      });
    } else if (animated()) {
      cards.forEach(enter);
    } else {
      scroller.scrollLeft = 0;
      state.scrollLeft = 0;
    }
  }

  state.shown = list;
  markLoaded(scroller);

  if (!state.seen) {
    // The drawer is closed (display: none), where lazy images never start: begin the first few now, quietly.
    list.slice(0, WARM_IMAGES).forEach((id) => {
      const image = nodes
        .get(id)
        ?.querySelector(".mcr-card__image:not(.is-loaded)");

      if (image) {
        image.fetchPriority = "low";
        image.loading = "eager";
      }
    });
  }

  setVisible(list.length > 0);
  updateNav();
}

function showPlaceholder() {
  if (hasPlaceholders()) return;

  const template = state.rail.querySelector("template[data-mcr-placeholder]");
  const scroller = track();

  closeSheet();
  scroller.replaceChildren(
    ...Array.from({ length: PLACEHOLDER_COUNT }, () =>
      template.content.firstElementChild.cloneNode(true),
    ),
  );
  scroller.scrollLeft = 0;
  state.scrollLeft = 0;
  state.shown = [];
  setVisible(true);
  updateNav();
}

function setVisible(visible) {
  const host = state.host;
  if (!host || host.hidden === !visible) return;

  if (!animated()) {
    host.hidden = !visible;
    return;
  }

  // The rail only appears or disappears in front of the shopper in rare cases (first results arriving late, or
  // every result gone): fold it open or shut instead of letting it jump.
  host.hidden = false;
  const height = host.scrollHeight;
  const collapsed = {
    height: "0px",
    opacity: 0,
    paddingBlock: "0px",
    borderBlockStartWidth: "0px",
  };
  const expanded = { height: `${height}px`, opacity: 1 };

  host.style.overflow = "hidden";
  const animation = host.animate(
    visible ? [collapsed, expanded] : [expanded, collapsed],
    {
      duration: 350,
      easing: EASE,
      fill: "forwards",
    },
  );

  animation.finished.then(
    () => {
      host.style.overflow = "";
      if (!visible && !state.shown.length && !hasPlaceholders())
        host.hidden = true;
      animation.cancel();
    },
    () => {},
  );
}

function updateNav() {
  if (!state.rail) return;

  const scroller = track();
  const nav = state.rail.querySelector(".mcr__nav");
  const [previous, next] = nav.querySelectorAll("[data-mcr-step]");
  const overflow = scroller.scrollWidth - scroller.clientWidth;
  const position = Math.abs(scroller.scrollLeft);

  nav.hidden = overflow <= 2 || hasPlaceholders();
  previous.disabled = position <= 2;
  next.disabled = position >= overflow - 2;
}

function markLoaded(scope) {
  scope
    .querySelectorAll(".mcr-card__image:not(.is-loaded)")
    .forEach((image) => {
      if (image.complete && image.naturalWidth)
        image.classList.add("is-loaded");
    });
}

/* Sync: decide between cached cards, a placeholder, or waiting ---------------------------------------------------- */

function apply() {
  if (state.host?.isConnected && state.rail) render(nextList(state.host));
}

function sync() {
  const host = state.host;
  if (!host?.isConnected || !state.rail) return;

  const anchors = host.anchors;
  const due = anchors.filter(needsLoad);
  const settled = Promise.all(
    due.map((anchor) => loadAnchor(anchor, drawerOpen() ? "high" : "low")),
  );

  if (due.length) {
    settled.then((changed) => changed.some(Boolean) && sync());
  }

  // First paint of an open drawer with an anchor that has never answered: hold the space, then show everything in
  // priority order at once rather than letting cards trickle in and shuffle.
  const reorderable = !(state.seen && state.shown.length);
  const unknown = due.some((anchor) => !cache().recs[anchor.id]);

  if (reorderable && unknown && drawerOpen()) {
    showPlaceholder();

    const token = (state.waitToken = {});
    Promise.race([
      settled,
      new Promise((resolve) => setTimeout(resolve, PLACEHOLDER_TIMEOUT)),
    ]).then(() => {
      if (state.waitToken === token) {
        state.waitToken = null;
        apply();
      }
    });

    return;
  }

  state.waitToken = null;
  apply();
}

function queueIdleSync() {
  if (state.idleQueued) return;
  state.idleQueued = true;

  const schedule = () => {
    const idle =
      window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
    idle(
      () => {
        state.idleQueued = false;
        sync();
      },
      { timeout: 2500 },
    );
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }
}

/* Interaction ------------------------------------------------------------------------------------------------------ */

function openSheet(card) {
  closeSheet();
  state.openCard = card;
  card.classList.add("is-open");
  card.querySelector("[data-mcr-open]")?.setAttribute("aria-expanded", "true");
  card.querySelector(".mcr-size")?.focus({ preventScroll: true });
}

function closeSheet({ restoreFocus = false } = {}) {
  const card = state.openCard;
  if (!card) return;

  state.openCard = null;
  card.classList.remove("is-open");

  const toggle = card.querySelector("[data-mcr-open]");
  toggle?.setAttribute("aria-expanded", "false");
  if (restoreFocus) toggle?.focus({ preventScroll: true });
}

function scrollByPage(direction) {
  const scroller = track();
  const card = scroller.querySelector(".mcr-card");
  if (!card) return;

  const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
  const stride = card.getBoundingClientRect().width + gap;
  const perPage = Math.max(
    1,
    Math.floor((scroller.clientWidth + gap) / stride),
  );
  const rtl = getComputedStyle(scroller).direction === "rtl" ? -1 : 1;

  scroller.scrollBy({
    left: direction * perPage * stride * rtl,
    behavior: reducedMotion.matches ? "auto" : "smooth",
  });
}

function announce(message) {
  const live = state.rail?.querySelector("[data-mcr-live]");
  if (live) live.textContent = message;
}

function hold(card) {
  const id = card.dataset.productId;

  closeSheet();
  card.classList.add("is-added");
  state.addedIds.add(id);
  clearTimeout(state.added?.timer);

  state.added = {
    id,
    timer: setTimeout(() => {
      state.added = null;
      apply();
    }, ADDED_HOLD),
  };
}

function fail(card, message) {
  const slot = card.querySelector("[data-mcr-error]");
  slot.textContent = message || "Could not add to cart";

  closeSheet();
  card.classList.add("has-error");
  announce(slot.textContent);

  clearTimeout(errorTimers.get(card));
  errorTimers.set(
    card,
    setTimeout(() => card.classList.remove("has-error"), 3000),
  );
}

async function addToCart(card, button) {
  if (card.classList.contains("is-busy") || card.classList.contains("is-added"))
    return;

  const variantId = button.dataset.variantId;
  const sections = [];

  card.classList.remove("has-error");
  card.classList.add("is-busy");
  button.setAttribute("aria-busy", "true");

  // Same contract as the theme's product form: bundle the sections that re-render on a cart change.
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections },
    }),
  );

  const body = new FormData();
  body.set("id", variantId);
  body.set("quantity", "1");
  body.set("sections", sections.join(","));
  body.set("sections_url", `${ROOT}variants/${variantId}`);

  try {
    const response = await fetch(`${ROOT}cart/add.js`, {
      method: "POST",
      body,
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    const item = await response.json();

    if (!response.ok) throw new Error(item.description || item.message || "");

    const cart = await (await fetch(`${ROOT}cart.js`)).json();
    cart.sections = item.sections;

    hold(card);
    announce(
      `${card.querySelector("[data-mcr-title]")?.textContent.trim() || item.product_title} added to cart.`,
    );

    card.dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: { items: [item], cart },
      }),
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: "variant:add", cart },
      }),
    );
  } catch (error) {
    fail(card, error.message);
  } finally {
    card.classList.remove("is-busy");
    button.removeAttribute("aria-busy");
  }
}

function onRailClick(event) {
  const target = event.target;
  const step = target.closest("[data-mcr-step]");

  if (step) {
    scrollByPage(Number(step.dataset.mcrStep));
    return;
  }

  const card = target.closest("[data-mcr-card]");
  if (!card) return;

  if (target.closest("[data-mcr-open]")) {
    if (card === state.openCard) {
      closeSheet({ restoreFocus: true });
    } else {
      openSheet(card);
    }
  } else if (target.closest("[data-mcr-close]")) {
    closeSheet({ restoreFocus: true });
  } else {
    const button = target.closest("[data-variant-id]");
    if (button) addToCart(card, button);
  }
}

function adopt(rail) {
  state.rail = rail;
  state.shown = [];
  state.scrollLeft = 0;

  const scroller = track();
  let frame = 0;

  rail.addEventListener("click", onRailClick);
  rail.addEventListener(
    "load",
    (event) =>
      event.target.classList?.contains("mcr-card__image") &&
      event.target.classList.add("is-loaded"),
    true,
  );

  scroller.addEventListener(
    "scroll",
    () => {
      if (frame) return;

      frame = requestAnimationFrame(() => {
        frame = 0;
        state.scrollLeft = scroller.scrollLeft;
        updateNav();
      });
    },
    { passive: true },
  );

  new ResizeObserver(() => updateNav()).observe(scroller);
}

/** The rail counts as seen from the first frame painted with the drawer open. */
function markSeenAfterPaint(drawer) {
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      state.seen = drawer.hasAttribute("open");
    }),
  );
}

function watchDrawer(drawer) {
  if (!drawer || watchedDrawers.has(drawer)) return;
  watchedDrawers.add(drawer);

  // The first add to an empty cart opens the drawer before this rail exists, so its before-show was missed.
  if (drawer.hasAttribute("open")) markSeenAfterPaint(drawer);

  // Not tied to the host's lifetime: the drawer element survives its own re-renders, the host does not.
  drawer.addEventListener("dialog:before-show", () => {
    state.seen = false;
    sync();
    markSeenAfterPaint(drawer);
  });

  drawer.addEventListener("dialog:after-hide", () => {
    state.seen = false;
    clearTimeout(state.added?.timer);
    state.added = null;
    state.addedIds.clear();
    closeSheet();
    sync(); // rebuild in priority order while nobody is looking
  });
}

/* Element ---------------------------------------------------------------------------------------------------------- */

class MaceooCartRecommendations extends HTMLElement {
  get anchors() {
    return (this.getAttribute("anchors") || "")
      .split(",")
      .filter(Boolean)
      .map((token) => {
        const [id, flag] = token.split(":");
        return { id, c: flag === "c" };
      });
  }

  get inCart() {
    return new Set(
      (this.getAttribute("in-cart") || "").split(",").filter(Boolean),
    );
  }

  get limit() {
    return Number.parseInt(this.getAttribute("limit"), 10) || 6;
  }

  connectedCallback() {
    const served = this.querySelector(":scope > .mcr");

    if (
      served &&
      served !== state.rail &&
      served.dataset.signature !== state.rail?.dataset.signature
    ) {
      adopt(served); // first render on this page, or the heading changed in the theme editor
    } else if (state.rail && state.rail.parentNode !== this) {
      this.replaceChildren(state.rail);
    }

    if (!state.rail) return;

    state.host = this;
    watchDrawer(this.closest("cart-drawer"));
    this.hidden = !(state.shown.length || hasPlaceholders());

    const scroller = track();
    if (state.scrollLeft && scroller.scrollLeft !== state.scrollLeft) {
      scroller.scrollLeft = state.scrollLeft;
    }

    if (drawerOpen()) {
      sync();
    } else {
      queueIdleSync();
    }
  }
}

if (!window.customElements.get("maceoo-cart-recommendations")) {
  window.customElements.define(
    "maceoo-cart-recommendations",
    MaceooCartRecommendations,
  );
}

/* Global listeners ------------------------------------------------------------------------------------------------- */

// Product forms (PDP, quick buy) carry Shopify's hidden product-id input.
document.addEventListener(
  "submit",
  (event) => {
    const form = event.target;

    if (
      form instanceof HTMLFormElement &&
      /\/cart\/add/.test(form.getAttribute("action") || "")
    ) {
      warm(form.elements["product-id"]?.value);
    }
  },
  true,
);

// Collection card quick add: size strip, touch bag button, theme quick buy.
document.addEventListener(
  "click",
  (event) => {
    const trigger = event.target.closest?.(
      ".quick-add-size-btn, .quick-add-mobile-btn, .product-card__quick-add-button",
    );
    if (trigger) warm(trigger.closest("product-card")?.dataset.productId);

    if (state.openCard && !state.openCard.contains(event.target)) closeSheet();
  },
  true,
);

// Escape closes an open size sheet before the drawer's focus trap sees it and closes the drawer.
window.addEventListener(
  "keydown",
  (event) => {
    if (event.key === "Escape" && state.openCard) {
      event.stopPropagation();
      closeSheet({ restoreFocus: true });
    }
  },
  true,
);
